import React, { Suspense, memo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { LanguageProvider } from './contexts/LanguageContext';
import { lazyWithRetry, clearChunkReloadFlag } from './utils/lazyWithRetry';
import { useIsMobile } from './hooks/useIsMobile';
import './styles/global.css';
import './styles/admin.css';

const Home = lazyWithRetry(() => import('./pages/Home'));
const Services = lazyWithRetry(() => import('./pages/Services'));
const Portfolio = lazyWithRetry(() => import('./pages/Portfolio'));
const About = lazyWithRetry(() => import('./pages/About'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const Team = lazyWithRetry(() => import('./pages/Team'));
const Careers = lazyWithRetry(() => import('./pages/Careers'));
const StartProject = lazyWithRetry(() => import('./pages/StartProject'));
const Privacy = lazyWithRetry(() => import('./pages/Privacy'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const Cookies = lazyWithRetry(() => import('./pages/Cookies'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const Admin = lazyWithRetry(() => import('./pages/Admin'));

const LoadingFallback = memo(() => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
));

LoadingFallback.displayName = 'LoadingFallback';

const AppRoutes = memo(() => {
  const location = useLocation();

  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  return (
    <motion.main
      key={location.pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="flex-grow relative z-10"
    >
      <Routes location={location}>
        <Route path="/" element={<Suspense fallback={<LoadingFallback />}><Home /></Suspense>} />
        <Route path="/services" element={<Suspense fallback={<LoadingFallback />}><Services /></Suspense>} />
        <Route path="/portfolio" element={<Suspense fallback={<LoadingFallback />}><Portfolio /></Suspense>} />
        <Route path="/about" element={<Suspense fallback={<LoadingFallback />}><About /></Suspense>} />
        <Route path="/contact" element={<Suspense fallback={<LoadingFallback />}><Contact /></Suspense>} />
        <Route path="/blog" element={<Suspense fallback={<LoadingFallback />}><Blog /></Suspense>} />
        <Route path="/team" element={<Suspense fallback={<LoadingFallback />}><Team /></Suspense>} />
        <Route path="/careers" element={<Suspense fallback={<LoadingFallback />}><Careers /></Suspense>} />
        <Route path="/start-project" element={<Suspense fallback={<LoadingFallback />}><StartProject /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<LoadingFallback />}><Privacy /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<LoadingFallback />}><Terms /></Suspense>} />
        <Route path="/cookies" element={<Suspense fallback={<LoadingFallback />}><Cookies /></Suspense>} />
        <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
      </Routes>
    </motion.main>
  );
});

AppRoutes.displayName = 'AppRoutes';

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Admin />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      <Navbar />
      <AppRoutes />
      <Footer />
    </div>
  );
}

function App() {
  const isMobile = useIsMobile();

  return (
    <LanguageProvider>
      <MotionConfig reducedMotion={isMobile ? 'always' : 'user'}>
        <Router>
          <ScrollToTop />
          <AppShell />
        </Router>
      </MotionConfig>
    </LanguageProvider>
  );
}

export default App;
